import React, { Suspense } from "react";
import { usePricingReturn } from "./pages/PricingPage";
import { motion, AnimatePresence } from "framer-motion";
import OccContextHeader from "@/components/ops/OccContextHeader";
import { CruiseProvider, useCruise } from "@/context/CruiseContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { hasPendingMicrosoftRedirect } from "@/lib/federatedAuth";
import Sidebar from "@/components/layout/Sidebar";
import { AuthenticatedHeader } from "@/components/layout/AuthenticatedHeader";
import OnboardingShell from "@/components/layout/OnboardingShell";
import SampleVoyageBanner from "@/components/shared/SampleVoyageBanner";
import MobileBottomTabBar from "@/components/layout/MobileBottomTabBar";
import NativeBridge from "@/components/layout/NativeBridge";
// MXR-004 · Route-level code splitting (2026-08-05). The landing
// dashboard (CruiseHome) stays eagerly imported so the first paint
// after login is instant. Every other workspace ships in its own
// webpack chunk via React.lazy, cutting the initial bundle for mobile
// travelers to roughly a third of the RC2 baseline.
import CruiseHome from "@/components/cruise-home/CruiseHome";
// RC8 Phase 1 Slice 3 · dedicated first-class mobile Today surface.
// Same canonical composed backend contract (`/api/cruises/current/today`);
// distinct mobile presentation from desktop CruiseHome.
import MobileToday from "@/components/mobile-today/MobileToday";
// RC8 Phase 1 Slice 4 · dedicated first-class mobile Voyage surface.
// Consumes the canonical multi-day composition (`/api/cruises/current/journey`).
// One canonical itinerary → many consumers. Distinct mobile presentation
// from desktop JourneyPage — desktop Journey remains untouched.
import MobileVoyage from "@/components/mobile-voyage/MobileVoyage";
// RC7 · M6 · dedicated first-class mobile Documents surface. Consumes
// the canonical safe-descriptor catalogue (`/api/mobile/documents/catalog`)
// and coordinates biometric-gated C3 reveal via `documentAssetStore.js`.
// Desktop DocumentsWorkspace remains untouched.
import MobileDocumentsScreen from "@/components/mobile-documents/MobileDocumentsScreen";
import { isNativePlatform } from "@/lib/platform";
// RC7 · M9 · Global mobile Safety FAB. One canonical emergency escape
// hatch anchored bottom-right on EVERY mobile route. Renders a full-
// height Safety Hub sheet with 8 emergency playbooks (bundled offline),
// Safety Memory, cruise-line contacts, and deep-links into the M6
// Documents surface.
import MobileSafetyFAB from "@/components/mobile-safety/MobileSafetyFAB";
// CCD-DASH-015 · REV-6 preserved as an architectural prototype
// (PO decision 2026-08-06). Mounted at `#dashboard-preview-rev6` so
// the operational-intelligence concepts (Voyage Readiness, Outstanding
// Actions, At-a-Glance rail) remain reviewable while CCD-DASH-015
// selectively integrates them into REV-5. Never linked from the sidebar.
import CruiseHomeRev6 from "@/components/cruise-home-preview/CruiseHomeRev6";
// RC8 · Dedicated Ship Guide surface (Widgety-backed ship reference). Opened
// from the compact "Explore ship" module on Voyage Home / Today.
import ShipGuide from "@/components/ship-reference/ShipGuide";
// IMPL-FAMILY-SHARING-PLAN · Milestone 1 · FTUE tone-validation preview
// (GOV-DASH-001 §5 · preview-first before in-place mount into Cruise Home).
// Reached only via `#family-sharing-preview` — not linked from the sidebar.
import FamilySharingPreview from "@/components/family-sharing/FamilySharingPreview";
// IMPL-FAMILY-SHARING-PLAN · Milestone 3 · Invitation Lifecycle
// (UX-ACC-FAMILY-SHARING-M3-INVITATION-LIFECYCLE, PO-authorised 2026-08-13).
// Three route components dispatched by the hash router:
//   #family-sharing/review/<invitation_id>  → editorial invitation review
//   #shared-with-me                          → first-party invitee workspace
//   #shared/<cruise_id>                      → read-only shared Cruise Home
// Kept lazy so the ~30 kB payload does not tax first paint for the
// existing traveler; invitees hit these routes after a full-page load
// from the copy-link URL, so a Suspense fallback is acceptable there.
const InvitationReviewPage   = React.lazy(() => import("@/components/family-sharing/InvitationReviewPage"));
const SharedWithMeWorkspace  = React.lazy(() => import("@/components/family-sharing/SharedWithMeWorkspace"));
const SharedCruiseSummary    = React.lazy(() => import("@/components/family-sharing/SharedCruiseSummary"));
import LoginPage from "@/pages/Login";
import Support from "@/pages/Support";
import AuthCallback from "@/pages/AuthCallback";
import MicrosoftAuthCallback from "@/pages/MicrosoftAuthCallback";
import DestinationPlaceholder from "@/components/shared/DestinationPlaceholder";
import CreateCruiseRoute from "@/components/cruises/create-workflow/CreateCruiseRoute";

// RC7 · Planning Dashboard retired 2026-02-11 (routing) + 2026-02-11
// (implementation). The `PlanningCenter` React.lazy binding has been
// removed. Legacy hash routes (`#planning-center`, `#binder`,
// `#planning`) continue to redirect to their canonical replacements
// below — see the retirement notes further down and
// `/app/memory/HERO_SYSTEM.md`.
const CruiseCenter            = React.lazy(() => import("@/components/cruise/CruiseCenter"));
const VoyageOverview          = React.lazy(() => import("@/components/cruise/VoyageOverview"));
const MemoriesCenter          = React.lazy(() => import("@/components/memories/MemoriesCenter"));
const JourneyPage             = React.lazy(() => import("@/components/journey/JourneyPage"));
const AdministrationWorkspace = React.lazy(() => import("@/components/admin/AdministrationWorkspace"));
const GovernanceWorkspace     = React.lazy(() => import("@/components/governance/GovernanceWorkspace"));
const CKBStudio               = React.lazy(() => import("@/components/ckb-studio/CKBStudio"));
const DestinationGuide        = React.lazy(() => import("@/components/destination-guide/DestinationGuide"));
const SeaDayGuide             = React.lazy(() => import("@/components/sea-day/SeaDayGuide"));
const KnowledgeQualityWorkspace = React.lazy(() => import("@/components/kqw/KnowledgeQualityWorkspace"));
const EditorialStudio         = React.lazy(() => import("@/components/editorial-studio/EditorialStudio"));
const JourneyIntelligence     = React.lazy(() => import("@/components/briefing/JourneyIntelligence"));
const BookingWorkspace        = React.lazy(() => import("@/components/booking/BookingWorkspace"));
const JourneyWorkspace        = React.lazy(() => import("@/components/journey/JourneyWorkspace"));
const DiningOpsWorkspace      = React.lazy(() => import("@/components/dining-ops/DiningOpsWorkspace"));
const EntitlementsOpsWorkspace = React.lazy(() => import("@/components/entitlements-ops/EntitlementsOpsWorkspace"));
const OperationsCommandCentre = React.lazy(() => import("@/components/ops/OperationsCommandCentre"));
const TravelersWorkspace      = React.lazy(() => import("@/components/travelers/TravelersWorkspace"));
const PackingWorkspace        = React.lazy(() => import("@/components/packing/PackingWorkspace"));
const FinancesWorkspace       = React.lazy(() => import("@/components/finances/FinancesWorkspaceRouter"));
// RC7 · Finance V2 (Phase 1) · gated by AppSettings.experimental.finance_v2.
const FinancesWorkspaceV2     = React.lazy(() => import("@/components/finances/FinancesWorkspaceV2"));
const DocumentsWorkspace      = React.lazy(() => import("@/components/documents/DocumentsWorkspace"));
// RC7 · Binder frontend components retired 2026-08-10 per
// `RC7-BINDER-MIGRATION-INVENTORY.md` §19.3. The 11 files under
// `frontend/src/components/binder/` were moved to
// `/app/archive/binder-2026-08/frontend/src/components/binder/`.
const _BinderWorkspaceRetired = null;
const ReservationsWorkspace   = React.lazy(() => import("@/components/reservations/ReservationsWorkspace"));
const CruisesWorkspace        = React.lazy(() => import("@/components/cruises/CruisesWorkspace"));
const SettingsWorkspace       = React.lazy(() => import("@/components/settings/SettingsWorkspace"));
const CruiseIntelligenceMap   = React.lazy(() => import("@/components/cruise-map/CruiseIntelligenceMap"));
const AddOnsWorkspace         = React.lazy(() => import("@/components/add-ons/AddOnsWorkspace"));
// CCD-FIN-016 Phase 1c completion (PO 2026-08-06) · Insurance is a
// first-class owner workspace. Finance projects insurance policies
// read-only; every write lives here.
const InsuranceWorkspace      = React.lazy(() => import("@/components/insurance/InsuranceWorkspace"));
import VesselMarkerPreview from "@/pages/VesselMarkerPreview";
import { DESTINATIONS } from "@/pages/destinations";
import { TESTIDS } from "@/lib/testIds";
import { retiredInviteLoginRequired } from "@/lib/legacyInviteUrl";
import "@/App.css";
// DDS-001 · Chapter 10 · Emergency capability. The safety colour
// tokens, floating Safety button, universal footer, and Emergency
// landing are governed by
// /app/docs/product-office/DDS-001-CHAPTER-10-EMERGENCY.md.
import "@/emergency.css";
import { EmergencyLandingPage } from "@/components/emergency/EmergencyLandingPage";
import { Loader2 } from "lucide-react";
// LAW-005 · Global toast host. Every RelationshipPicker, save modal,
// and future workflow surface uses `sonner` to deliver in-context
// feedback. The Toaster is mounted once at the very top of the app
// tree so notifications survive route transitions and modal state.
import { Toaster } from "@/components/ui/sonner";

// MXR-004 · Editorial fallback shown while a route chunk is fetching.
// Kept intentionally light — a two-line skeleton so the layout does
// not jump when the real workspace resolves.
function RouteChunkFallback() {
  return (
    <div className="animate-pulse space-y-3" aria-live="polite" aria-busy="true">
      <div className="h-8 w-2/3 rounded bg-slate-200/70" />
      <div className="h-56 w-full rounded-2xl bg-slate-100/80" />
      <div className="sr-only">Loading workspace…</div>
    </div>
  );
}

const Content = ({ route, stage, hasCapability, setRoute }) => {
  // Sprint 4 · Slice A — Voyage Home is now the canonical landing
  // dashboard, consuming the Traveler Context Service
  // (`GET /api/traveler-context/me/current`). It renders phase, the
  // ADR-TIME-001 three-clock strip, next-event countdown, and
  // phase-driven recommendations. Users without an active voyage see
  // a friendly empty state with a link to the voyage picker.
  //
  // Retained: PlanningCenter / CruiseCenter / MemoriesCenter live on
  // as their own routes (see `journey`, `voyages`, etc.) — Voyage
  // Home is a compact traveler-facing overlay, not their
  // replacement.
  const voyageLike = stage === "voyage" || stage === "port-day";
  // RC8 Phase 1 Slice 3 · Mobile Today surface. Renders on the ratified
  // 5-tab IA (Decision D2). Consumes the same canonical `/today` and
  // `/ship-time` endpoints as desktop — no second source of truth.
  if (route === "today") {
    const isMobile =
      typeof window !== "undefined" && window.innerWidth < 768;
    return isMobile ? <MobileToday /> : <CruiseHome />;
  }
  if (route === "dashboard") return <CruiseHome />;
  // RC8 · Ship Guide — dedicated routed page (full-screen on mobile) hosting
  // the traveler's booked cruise details + the complete Widgety catalogue.
  if (route === "ship-guide") return <ShipGuide />;
  // RC8 · `ship-guide:<tab>` deep-link (e.g. Weekly Briefing "Cabin assigned" → Decks).
  if (route.startsWith("ship-guide:")) return <ShipGuide initialTab={route.slice("ship-guide:".length)} />;
  // CCD-DASH-015 · REV-6 architectural prototype (PO decision 2026-08-06).
  // Preserved so the Voyage Readiness / Outstanding Actions / At-a-Glance
  // rail concepts stay reviewable during the selective-integration
  // workstream. `#dashboard` is REV-5 (authoritative editorial dashboard).
  if (route === "dashboard-preview-rev6") return <CruiseHomeRev6 />;
  if (route === "family-sharing-preview") return <FamilySharingPreview />;
  // M3 · Invitation Lifecycle (PO-authorised 2026-08-13). Hash router
  // splits on `?` before returning the route slug, so `#family-sharing/
  // review/<invitation_id>` arrives here as `family-sharing/review/<id>`.
  if (route === "shared-with-me") return <SharedWithMeWorkspace />;
  if (route.startsWith("family-sharing/review/")) {
    const invitationId = route.slice("family-sharing/review/".length);
    return <InvitationReviewPage invitationId={invitationId} />;
  }
  if (route.startsWith("shared/")) {
    const cruiseId = route.slice("shared/".length);
    return <SharedCruiseSummary cruiseId={cruiseId} />;
  }
  // T1 · Feb 2026 · Canonical `cruise-*` routes; `voyage-*` aliases
  // below remain so bookmarks age out gracefully. Delete after T2.
  if (route === "cruise-center") return <CruiseCenter />;
  if (route === "cruise-map") return <CruiseIntelligenceMap />;
  // Legacy stage-based centres are still reachable through explicit
  // deep-links for editorial / power-user workflows.
  // RC7 · Legacy Planning Dashboard RETIRED (PO 2026-02-11). All
  // `#binder`, `#planning-center`, and default-fallback traffic now
  // routes to the canonical Briefing (`JourneyIntelligence`) — the
  // authoritative intelligence surface. Voyage identity, weekly
  // intelligence, "What Matters", and destination guides all live
  // there. See `RC7-BINDER-MIGRATION-INVENTORY.md`.
  if (route === "planning-center" || route === "binder") return <JourneyIntelligence />;
  if (route === "voyage-center") return <CruiseCenter />;
  if (route === "memories-center") return <MemoriesCenter />;
  if (route === "journey") {
    // RC8 Phase 1 Slice 4 · Mobile Voyage first-class surface.
    // Same canonical multi-day contract as the desktop Journey — never
    // a mobile-specific endpoint. Desktop Journey (JourneyPage) is frozen
    // per RC8 governance.
    const isMobile =
      typeof window !== "undefined" && window.innerWidth < 768;
    return isMobile ? <MobileVoyage /> : <JourneyPage />;
  }
  if (route === "travelers") return <TravelersWorkspace />;
  if (route === "packing") return <PackingWorkspace />;
  if (route === "dining") return <ReservationsWorkspace kind="dining" />;
  if (route === "excursions") return <ReservationsWorkspace kind="excursions" />;
  // Sprint 5 · Voyage Intelligence Map — full-page interactive route
  // surface with a Day 1 → Day N timeline scrubber, animated ship, and
  // clickable port markers that open a Port Focus sheet.
  if (route === "voyage-map") return <CruiseIntelligenceMap />;
  // PO nav refactor 2026-02-20 · Add-Ons is a first-class operational
  // workspace and the commercial system of record for prepaid onboard
  // products. The former "Cruise Planner" preparation dashboard has
  // been retired; readiness lives on the Dashboard, operational
  // planning lives in its dedicated domain workspaces. Legacy hash
  // routes redirect to `add-ons` transparently.
  if (route === "add-ons") return <AddOnsWorkspace />;
  // CCD-FIN-016 Phase 1c completion · Insurance owner workspace.
  if (route === "insurance") return <InsuranceWorkspace />;
  if (route === "cruise-planner") return <AddOnsWorkspace />;   // legacy alias
  if (route === "voyage-planner") return <AddOnsWorkspace />;   // legacy alias
  if (route === "planning") return <AddOnsWorkspace />;         // legacy alias
  // LAW-004 · Feb 2026 · "Finances" is the canonical Travel Finance
  // workspace. The legacy `budget` route key resolves here so any
  // pre-refactor bookmarks / deep links still work. The former
  // Budget concept has been retired — see /app/memory/laws/LAW-004.
  if (route === "finances" || route === "budget") return <FinancesWorkspace setRoute={setRoute} />;
  // Phase 4 · Document Intelligence Pipeline · Documents Workspace.
  // The operational hub for every uploaded document — cruise, flight,
  // hotel, dining, excursion, insurance, transportation, rental car,
  // parking. LAW-004 · Documents are first-class objects.
  if (route === "documents") {
    // RC7 · M6 · Mobile-first-class Documents surface. Desktop retains
    // the operational DocumentsWorkspace; mobile viewport gets the
    // canonical safe-descriptor catalogue with biometric-gated C3 reveal.
    const isMobile =
      typeof window !== "undefined" && window.innerWidth < 768;
    // Biometric document reveal belongs to the native shell. Small browser
    // windows still need the authorized web document metadata/download flow.
    return isMobile && isNativePlatform() ? <MobileDocumentsScreen /> : <DocumentsWorkspace />;
  }
  // RC7 · `route === "binder"` is no longer a routable target. Any legacy
  // URL fragment falls through to the default dashboard render below.
  if (route === "voyages") return <CruisesWorkspace />;
  // ADR-CRUISE-001 · Sprint 2 · The Create Cruise Workflow is the
  // permanent creation surface. Restored + modernized from the
  // Foundation-Freeze deletion. `#cruises/new` is the canonical
  // deep-link; `#voyages/new` aliases to the same route.
  if (route === "cruises/new" || route === "voyages/new") return <CreateCruiseRoute />;
  if (route === "settings") return <SettingsWorkspace />;
  // Sidebar 'Voyage' item — always operational. `CruiseCenter` remains the
  // live "day-by-day" dashboard for in-voyage stage; every other stage now
  // routes to the operational `VoyageOverview` (Iteration 18 · P0 fix) so
  // pre-voyage and post-voyage no longer render the maturity-showcase
  // placeholder.
  if (route === "voyage") {
    return voyageLike ? <CruiseCenter /> : <VoyageOverview />;
  }
  if (route === "administration") {
    return hasCapability("admin:users:read") ? <AdministrationWorkspace /> : null;
  }
  if (route === "governance") {
    return hasCapability("governance:read") ? <GovernanceWorkspace /> : null;
  }
  if (route === "knowledge") {
    // Retired · legacy Knowledge workspace consolidated into the
    // Editorial Knowledge (CKB Studio) experience. `#/knowledge`
    // bookmarks resolve through the redirect in <Shell>.
    return hasCapability("knowledge:read") ? <CKBStudio /> : null;
  }
  if (route === "ckb-studio") {
    return hasCapability("knowledge:read") ? <CKBStudio /> : null;
  }
  // Sprint 7 · Journey Intelligence — the personalised daily briefing.
  if (route === "briefing") {
    return <JourneyIntelligence />;
  }
  // Destination Guide — the flagship editorial surface (Sprint 4 · P1).
  // Route format: `destination-guide:<ckb-destination-id>`. Rendered
  // for anyone (traveler or editor) — it's the traveler-facing view.
  if (route.startsWith("destination-guide:")) {
    const id = route.slice("destination-guide:".length);
    return id ? <DestinationGuide destinationId={id} /> : null;
  }
  // Sprint 9 · Sea Day Guide — traveler-facing sea-day editorial surface.
  // Route: `sea-day:<ckb-sea-day-id>`. Reuses the Editorial Presentation
  // System — no bespoke layout.
  if (route.startsWith("sea-day:")) {
    const id = route.slice("sea-day:".length);
    return id ? <SeaDayGuide seaDayId={id} /> : null;
  }
  // Sprint 10 · Knowledge Quality Workspace — internal editorial operations
  // dashboard. Not traveler-facing. Observes the knowledge graph.
  if (route === "kqw") {
    return <KnowledgeQualityWorkspace />;
  }
  // Sprint 13 · Editorial Studio — Media Manager. Route format:
  //   #editorial-studio                     (landing)
  //   #editorial-studio:<type>:<id>         (subject workspace)
  if (route === "editorial-studio" || route.startsWith("editorial-studio:")) {
    return hasCapability("media:manage") ? <EditorialStudio /> : null;
  }
  if (route === "booking") {
    return hasCapability("knowledge:read") ? <BookingWorkspace /> : null;
  }
  if (route === "journey-ops") {
    return hasCapability("knowledge:read") ? <JourneyWorkspace /> : null;
  }
  if (route === "dining-ops") {
    return hasCapability("knowledge:read") ? <DiningOpsWorkspace /> : null;
  }
  if (route === "entitlements-ops") {
    return hasCapability("knowledge:read") ? <EntitlementsOpsWorkspace /> : null;
  }
  if (route === "command-centre") {
    // OCC targets contain a complete hash and queue query. setRoute accepts
    // only a route slug, so passing the full target there creates ##... and
    // bypasses the dispatcher. Native hash navigation also notifies receivers.
    return hasCapability("knowledge:read") ? <OperationsCommandCentre onNavigate={(cta) => { if (cta?.target) window.location.hash = cta.target; }} /> : null;
  }
  // Retired · Legacy Knowledge Prototype and `#/knowledge-prototype`
  // deep links resolve into the canonical Knowledge workspace (CKB
  // Studio) via the transparent redirect in <Shell>.
  if (route === "knowledge-prototype") {
    return hasCapability("knowledge:read") ? <CKBStudio /> : null;
  }
  // CC-ICON-001 (proposed) · Design System preview surface for the
  // Cruise Command Vessel Marker concepts. Internal / product-office
  // review only — not linked from the sidebar. Deep-link via
  // `#design/vessel-marker`.
  if (route === "design/vessel-marker") {
    return <VesselMarkerPreview />;
  }
  // DDS-001 · Chapter 10 · §3 · The Emergency landing is a global
  // capability opened from the permanent "Emergency" navigation item
  // (Sidebar, directly above Settings). Deep-link via `#emergency`.
  // Governed surface — every visual and interactive decision must
  // reference DDS-001-CHAPTER-10-EMERGENCY.md.
  if (route === "emergency") {
    return <EmergencyLandingPage />;
  }
  // RC7 Briefing Engine (PO 2026-02-11) · dedicated Weekly Briefing
  // reader. Route form: `briefing:<id>`. The reader owns its own
  // fetch and enforces voyage isolation server-side. Deep-link via
  // `#briefing:<id>`.
  if (route && route.startsWith("briefing:")) {
    const briefingId = route.slice("briefing:".length);
    const BriefingReader = require("./components/briefing/BriefingReader").default;
    return <BriefingReader key={briefingId} briefingId={briefingId} />;
  }
  const config = DESTINATIONS[route];
  if (config) return <DestinationPlaceholder {...config} />;
  // RC7 · Default fallback for unmatched routes now lands on the
  // canonical Briefing (`JourneyIntelligence`) instead of the retired
  // Planning Dashboard. In-voyage and post-voyage stages keep their
  // dedicated Center surfaces.
  return stage === "planning" ? <JourneyIntelligence /> : voyageLike ? <CruiseCenter /> : <MemoriesCenter />;
};

const Shell = () => {
  const { stage, route, setRoute, voyage } = useCruise();
  const { hasCapability, user } = useAuth();

  // RC7 · M7 · Install the sync-engine connectivity hooks once. The
  // scope resolver reads the *current* voyage/user via closures over
  // React state so trigger events always see the latest values.
  React.useEffect(() => {
    (async () => {
      const eng = await import("@/mobile/offline/syncEngine");
      eng.installSyncEngineHooks(() => ({
        userId: user?.id || null,
        voyageId: voyage?.id || null,
      }));
    })();
  }, [user?.id, voyage?.id]);

  // Day 5 · Onboarding state — loaded on mount.
  const [onboarding, setOnboarding] = React.useState({ loaded: false });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/state`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setOnboarding({ loaded: true, ...d });
      } catch {
        if (!cancelled) setOnboarding({ loaded: true, has_voyage: true, tier: "subscriber" });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // RC7 · Legacy Binder Final-Edition publication surface RETIRED
  // (`RC7-BINDER-MIGRATION-INVENTORY.md` §19.12, PO-ratified 2026-08-10).
  // Journey remains core; other Journey surfaces (JourneyPage,
  // JourneyWorkspace, JourneyIntelligence) continue to read
  // `/api/journey/state` where they need it.

  // Capability guard: if current route requires a capability the user lacks,
  // redirect to dashboard.
  React.useEffect(() => {
    const restricted = {
      administration: "admin:users:read",
      governance: "governance:read",
    };
    const need = restricted[route];
    if (need && !hasCapability(need)) {
      setRoute("dashboard");
    }
  }, [route, hasCapability, setRoute]);

  // Legacy Knowledge routes → canonical Editorial Knowledge (CKB
  // Studio). Rewrites the hash silently so bookmarks converge on the
  // new address and users see exactly one Knowledge capability.
  // Users without knowledge:read fall through to dashboard rather than
  // rendering an unauthorised blank screen (governed no-broken-links).
  React.useEffect(() => {
    if (route !== "knowledge" && route !== "knowledge-prototype") return;
    const target = hasCapability("knowledge:read") ? "ckb-studio" : "dashboard";
    try {
      window.history.replaceState(null, "", `#${target}`);
    } catch (_e) { /* ignore */ }
    setRoute(target);
  }, [route, hasCapability, setRoute]);

  // All hooks are declared above.  From here down, conditional returns
  // are safe.  Day 5 onboarding gate:
  const showOnboarding = onboarding.loaded && !onboarding.has_voyage;
  if (showOnboarding) {
    const planOwn = () => {
      setOnboarding((s) => ({ ...s, has_voyage: true }));
      setRoute("cruises/new");
    };
    // No-voyage accounts: <OnboardingShell/> keeps Settings / Sign out
    // reachable from the shared header (route-aware), no cruise required.
    return (
      <OnboardingShell
        onPlanOwn={planOwn}
        onDemoReady={() => {
          setOnboarding({ loaded: false });
          window.location.hash = "dashboard";
          window.location.reload();
        }}
      />
    );
  }

  // RC7 · The disembarked-voyage Binder Final-Edition publish gate is
  // retired (`RC7-BINDER-MIGRATION-INVENTORY.md` §19.12). Journey
  // navigation, itinerary, and voyage-context remain untouched — the
  // dashboard is now the direct destination after onboarding.

  const key = route === "dashboard" ? `dashboard:${stage}` : route;

  return (
    <div
      data-testid={TESTIDS.appRoot}
      className="min-h-screen w-full bg-[#F4F7F9] text-slate-900"
    >
      <Sidebar />
      {/* MXR-002 · Content clearance — the arbitrary `pb-[...]` already
          sums the safe-area inset with the 4.5rem tab-bar height, so
          we drop the separate `cc-safe-bottom` class (whose plain-CSS
          padding-bottom competes with the Tailwind utility in the
          cascade and would zero the arbitrary utility out). */}
      <main className="md:ml-64 min-h-screen pb-[calc(env(safe-area-inset-bottom)+4.5rem)] md:pb-0">
        <AuthenticatedHeader />
        <AnimatePresence mode="wait">
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
            className="px-4 sm:px-6 md:px-8 lg:px-12 pt-3 md:pt-5 pb-10"
          >
            {/* MXR-004 · Route-level Suspense boundary. Each lazy
                workspace chunk resolves inside this boundary, so a
                fallback strip appears (never a blank screen) while
                the traveler waits for the chunk to arrive on flaky
                mobile networks. */}
            <Suspense fallback={<RouteChunkFallback />}>
              <OccContextHeader />
              <SampleVoyageBanner />
              <Content route={route} stage={stage} hasCapability={hasCapability} setRoute={setRoute} />
            </Suspense>
          </motion.div>
        </AnimatePresence>
      </main>
      {/* MXR-002 · Bottom tab bar — the primary mobile navigation
          surface. Native travel apps (Royal Caribbean, Flighty,
          Apple Wallet) all use this pattern; the hamburger drawer
          stays for secondary routes. Hidden on md+ where the
          sidebar dominates. */}
      <MobileBottomTabBar />
      {/* DDS-001 · Chapter 10 · Emergency now lives permanently in the
          navigation menu (Sidebar item, directly above Settings) — the
          floating desktop Safety button was removed so it never covers
          maps, cards or drawers. Mobile keeps the richer RC7-M9 Safety
          Hub sheet (playbooks + contacts + medical), a separate feature. */}
      {route !== "emergency" &&
        typeof window !== "undefined" &&
        window.innerWidth < 768 && (
          <MobileSafetyFAB setRoute={setRoute} />
        )}
    </div>
  );
};

function AuthGate() {
  const { loading, user, googleCallbackPending } = useAuth();
  const returningToPlan = usePricingReturn(user);

  // Payment-result routes must render regardless of auth state — Stripe
  // returns the traveller here after Checkout completes.
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (typeof window !== "undefined" && retiredInviteLoginRequired()) {
    return <LoginPage />;
  }
  if (path === "/payment/success") {
    const { PaymentSuccess } = require("./pages/PaymentResult");
    return <PaymentSuccess />;
  }
  if (path === "/payment/cancel") {
    const { PaymentCancel } = require("./pages/PaymentResult");
    return <PaymentCancel />;
  }
  if (path === "/pricing" || path === "/pricing/") {
    const PricingPage = require("./pages/PricingPage").default;
    return <PricingPage />;
  }

  // Public, unauthenticated compliance surface — Google Play + Apple
  // 5.1.1(v). Direct-URL, no session, no SSO. Renders exclusively at
  // /account-deletion and every trailing-slash / query variant.
  if (path === "/account-deletion" || path.startsWith("/account-deletion/")) {
    const AccountDeletionPage = require("./pages/AccountDeletion").default;
    return <AccountDeletionPage />;
  }

  // Commercial Launch Readiness · 2026-08-09 · Founding Voyager Kit —
  // /founding/<invite-code> is the QR-code landing surface printed on
  // every Founding Voyager Kit (PDF + Canva).  V1.0 policy: recognise
  // the path and route the visitor to the marketing landing with the
  // invite code preserved in sessionStorage so it can be attached
  // later to a Stripe metadata field once activation begins.
  if (path.startsWith("/founding/")) {
    const code = path.slice("/founding/".length).split("/")[0] || "";
    try {
      if (code && typeof window !== "undefined") {
        window.sessionStorage.setItem("cc-founding-invite", code);
      }
    } catch (_e) { /* private-mode / cookies disabled */ }
  }

  // Microsoft Entra uses `loginRedirect()`, so the return leg lands in
  // the current tab with `#code=…` (or `#error=…`) in the URL. When a
  // pending record is stashed in sessionStorage OR the URL fragment
  // carries an MSAL response, hand off to <MicrosoftAuthCallback>
  // which drives `handleRedirectPromise()` and finalises the session
  // against the open-registration endpoint at
  // `/api/auth/sso/microsoft/signin`.
  if (hasPendingMicrosoftRedirect()) {
    return <MicrosoftAuthCallback />;
  }

  // PD-028 · If Emergent Google Auth redirected us back with a session_id
  // in the URL fragment, hand off to AuthCallback synchronously — before
  // any /me probe or login gate — to avoid race conditions.
  if (googleCallbackPending || (typeof window !== "undefined" && window.location.hash?.includes("session_id="))) {
    return <AuthCallback />;
  }
  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#0b132b] text-white">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-[13px] tracking-[0.2em] font-semibold text-white/70 uppercase">
          Loading Cruise Command
        </span>
      </div>
    );
  }
  if (!user) {
    // The approved editorial opening spread is the canonical public sign-in
    // surface and already contains the live Google/Microsoft controls.
    const MarketingLanding = require("./pages/MarketingLanding").default;
    return <MarketingLanding />;
  }
  if (returningToPlan) return null;

  // RC-LAUNCH-COMMERCIAL-HARDENING-001 · Stage 2B.1 · Early Access
  // Invitation redemption. Authenticated-only by construction — this
  // branch sits AFTER the `!user` gate, so anonymous visitors sign in first.
  if (path === "/access-invitation" || path.startsWith("/access-invitation/")) {
    const AccessInvitationPage = require("./pages/AccessInvitation").default;
    return <AccessInvitationPage />;
  }

  return (
    <CruiseProvider>
      <Shell />
    </CruiseProvider>
  );
}

export default function App() {
  // Public support renders before authentication and voyage providers.
  if (typeof window !== "undefined" && /^\/support\/?$/.test(window.location.pathname)) {
    return <Support />;
  }
  return (
    <AuthProvider>
      {/* MXR-005 · Native bridge — no-op on web; wires deep-link +
          hardware-back handlers when running inside Capacitor. */}
      <NativeBridge />
      <AuthGate />
      <Toaster />
    </AuthProvider>
  );
}
